import { LINE_HARL, LINE_NAMES } from '@somnhold/content';
import { imin, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy, FieldPt, Hero, LineRules, LineState } from '../types.ts';
import { ev, findEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { cubBurst, cubSpawn, cubStep, cubsAlive } from './cubs.ts';
import { lineRight } from './pass-layout.ts';
import { standBy, standDone } from './wedges.ts';

/**
 * The camp and Ruy's line (docs/sendas/guerrero.md, chapter 5, act 2: «El campamento»; the puzzle in §4, «La línea de
 * Ruy»). At the pass's mouth, the Company's last camp: Ruy on his stone with his shield, and round him, camped, what came
 * in through the crack (skeletons, slingers, two shadow ogres): quiet until the hero comes near or strikes. Cleared,
 * torches come up the shepherds' path: Dalmo, the mule, the stretcher with nine shields, a dozen farmers. The tenth is
 * Ruy's, by his stone. The line is laid: eleven slots in the rock, Harl's (the middle) empty; a shield is taken from the
 * stretcher (or Ruy's stone) and set in a slot, or taken out of one, standing by it a moment. The diary's pages come one
 * by one (the HUD shows them); a counter says how many stand right, not which. All ten right: they light red and it is
 * won. All ten in and the hero says so: the line stays as it is. Cubs come one at a time and knock a shield back to the
 * stretcher if they reach it. Lost with the hero or Harl down.
 *
 * Cost: a handful of nightmares; the counter is ten lookups when a shield moves.
 */

const RUY = LINE_NAMES.indexOf('ruy');
/** A slot (index), a name on the stretcher (`TAKE` + its index), or the word «así queda» (`SETTLE`). */
export const LINE_TAKE = 100;
export const LINE_SETTLE = 200;

const fresh = (): LineState => ({ phase: 0, done: -1, t1: -1, on: new Array<number>(11).fill(-1), pile: LINE_NAMES.map(() => 0), hand: -1, at: -1, held: 0, right: false, cub: -1, cubs: [] });

/** Where a name's shield lies to be taken: the stretcher, or Ruy's by his stone. */
const pileAt = (r: LineRules, w: LineState, who: number): FieldPt => (w.pile[who] === 2 ? r.ruy : r.stretcher);

/** All ten shields in the line. */
export const lineFull = (w: LineState): boolean => w.on.filter((x) => x >= 0).length === LINE_NAMES.length;

/** The hero sent to a slot, to a name on the stretcher, or to say «así queda» (the `mark` command). */
export const lineOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.line;
  const w = s.senda?.line;
  if (!r || !w || w.phase !== 2) return;
  if (at === LINE_SETTLE) {
    if (!lineFull(w)) return;
    w.phase = 3;
    w.done = s.tick + 12;
    (s.senda as NonNullable<BattleState['senda']>).nums.line = lineRight(w.on);
    ev(s, { t: 'senda', kind: 'settle', npc: '', x: h.x, y: h.y, n: lineRight(w.on) });
    return;
  }
  let p: FieldPt | undefined;
  if (at >= LINE_TAKE) {
    const who = at - LINE_TAKE;
    if (!w.pile[who]) return;
    p = pileAt(r, w, who);
  } else {
    if (at === LINE_HARL - 1 || at < 0 || at > 10) return;
    p = r.slots[at];
  }
  if (!p) return;
  w.at = at;
  w.held = 0;
  orderMove(s, h, p.x, p.y + 500);
  h.target = 0;
  h.forced = false;
};

/** The hero carrying a shield (it goes at the rule's `carryBp`). */
export const lineCarrying = (s: BattleState): boolean => (s.senda?.line?.hand ?? -1) >= 0;

const handWork = (s: BattleState, r: LineRules, w: LineState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.at < 0) return;
  const at = w.at;
  const p = at >= LINE_TAKE ? pileAt(r, w, at - LINE_TAKE) : r.slots[at];
  if (!p || !standBy(h, w, p.x, p.y, r.reach)) return;
  if (!standDone(w, r.put)) return;
  if (at >= LINE_TAKE) {
    const who = at - LINE_TAKE;
    if (!w.pile[who]) return;
    // Whatever was in the hands goes back on the stretcher.
    if (w.hand >= 0) w.pile[w.hand] = 1;
    if (w.pile[who] === 2) cue('tenth');
    w.pile[who] = 0;
    w.hand = who;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: p.x, y: p.y, n: who });
    return;
  }
  const was = w.on[at] as number;
  if (w.hand < 0 && was < 0) return;
  w.on[at] = w.hand;
  w.hand = was;
  ev(s, { t: 'senda', kind: 'plant', npc: '', x: p.x, y: p.y, n: at });
  cue('plant');
  if (lineRight(w.on) === LINE_NAMES.length) {
    w.right = true;
    w.phase = 3;
    w.done = s.tick + 30;
    (s.senda as NonNullable<BattleState['senda']>).nums.line = LINE_NAMES.length;
    ev(s, { t: 'senda', kind: 'lineRed', npc: '', x: p.x, y: p.y });
    cue('right');
  }
};

/** The camp's nightmares lie quiet until the hero comes near or strikes one (true: nothing else for it this tick). */
export const lineHold = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.line;
  if (!w || !s.rules?.senda?.line) return false;
  if (w.cubs.some((c) => c.id === e.id)) return true;
  if (w.phase !== 0) return false;
  e.anim = 'idle';
  return true;
};

export const lineTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.line;
  const st = s.senda;
  if (!r || !st || !s.started) return;
  const w = (st.line ??= fresh());
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (w.phase === 0) {
    for (const n of st.npcs) if (n.id !== 'harlOld' && n.id !== 'ruy') n.away = true;
    if (s.wave.next < s.wave.spawns.length) return;
    // Woken: the hero near one of them, or one of them struck.
    const near = s.enemies.some((e) => e.hp > 0 && (e.hp < e.maxHp || (h && withinRange(h.x, h.y, e.x, e.y, 3500))));
    if (near || !s.enemies.some((e) => e.hp > 0)) {
      w.phase = 1;
      cue('wake');
    }
    return;
  }
  if (w.phase === 1) {
    if (s.enemies.some((e) => e.hp > 0)) return;
    // Cleared: the farms come up the shepherds' path with the stretcher.
    w.phase = 2;
    w.t1 = s.tick;
    w.cub = s.tick + r.cubs.first;
    LINE_NAMES.forEach((_, i) => (w.pile[i] = i === RUY ? 2 : 1));
    st.npcs.forEach((n, i) => {
      if (n.id === 'harlOld' || n.id === 'wenn' || n.id === 'ruy') return;
      n.away = false;
      n.x = r.path.x + (i % 3) * 500;
      n.y = r.path.y + (i % 2) * 400;
    });
    ev(s, { t: 'senda', kind: 'torches', npc: '', x: r.path.x, y: r.path.y });
    cue('clear');
    return;
  }
  // The farms walk up to the stretcher and stand round it (they don't know where each one went: they don't help).
  st.npcs.forEach((n, i) => {
    if (n.away || n.id === 'harlOld' || n.id === 'wenn' || n.id === 'ruy') return;
    const tx = r.stretcher.x + ((i % 3) - 1) * 650;
    const ty = r.stretcher.y + 600 + (i % 2) * 450;
    n.moving = !withinRange(n.x, n.y, tx, ty, 80);
    if (n.moving) stepToward(n, tx, ty, 40);
  });
  if (w.phase === 3) return;
  handWork(s, r, w, cue);
  // A cub now and then from up the pass, for a shield in the line.
  if (s.tick >= w.cub) {
    w.cub = s.tick + r.cubs.every;
    const full: number[] = [];
    w.on.forEach((x, i) => x >= 0 && full.push(i));
    const to = full.length ? (full[rngInt(s.rng, full.length)] as number) : imin(10, rngInt(s.rng, 11));
    cubSpawn(s, w.cubs, r.top, to, r.cub.hp);
  }
  cubsAlive(s, w.cubs);
  for (const c of w.cubs) {
    const e = findEnemy(s, c.id);
    const p = r.slots[c.to];
    if (!e || !p) continue;
    if (withinRange(e.x, e.y, p.x, p.y, 300)) {
      const who = w.on[c.to] as number;
      if (who >= 0) {
        w.on[c.to] = -1;
        w.pile[who] = 1;
        ev(s, { t: 'senda', kind: 'knock', npc: '', x: p.x, y: p.y, n: c.to });
        cue('knock');
      }
      cubBurst(s, e);
      continue;
    }
    cubStep(s, e, p.x, p.y, r.cub, 28);
  }
};

/** Won once the line is laid (all ten right, or the hero said so). */
export const lineResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.line;
  return w && w.phase === 3 && s.tick >= w.done ? 'won' : null;
};

/** The camp's nightmares, then how many stand right. */
export const lineGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.line;
  if (!w) return { done: 0, need: 10 };
  return { done: w.phase >= 2 ? lineRight(w.on) : 0, need: 10 };
};

/** How many pages of the diary are out (one now, the next every `page`). */
export const linePagesOut = (s: BattleState): number => {
  const r = s.rules?.senda?.line;
  const w = s.senda?.line;
  if (!r || !w || w.t1 < 0) return 0;
  return imin(r.pages.length, 1 + Math.trunc((s.tick - w.t1) / r.page));
};
